// animations/entrance-animations.ts
import gsap from 'gsap';
import { baseFade, baseFlip, baseFlap, baseBounce, baseExpand } from './common-base-animations';
import { directionToDegree } from './dot-cms-aniamtion.helper';
import { AnimationDynamicSettings, AnimationType } from './animation/animations.model';

export const entranceAnimations: Partial<
  Record<AnimationType, (s: AnimationDynamicSettings, el: Element, opts: gsap.TweenVars) => gsap.core.Tween>
> = {
  // Use common base animations
  fade: (_s, el, opts) => baseFade(el, opts),
  flip: (s, el, opts) => baseFlip(el, { ...opts, direction: s.direction }),
  flap: (_s, el, opts) => baseFlap(el, opts),
  bounce: (_s, el, opts) => baseBounce(el, opts),
  expand: (_s, el, opts) =>
    baseExpand(el, {
      ...opts,
      scale: typeof (opts as any).scale === 'string' ? parseFloat((opts as any).scale) : (opts as any).scale,
    }),
  // Entrance-specific animations
  glide: (s, el, opts) => {
    const safe = <T>(val: T | undefined, def: T): T => (val !== undefined ? val : def);
    const angle = directionToDegree(s.direction ?? 'up');
    const dist = safe(s.distance, 100);

    return gsap.fromTo(
      el,
      {
        x: -Math.cos(angle) * dist,
        y: -Math.sin(angle) * dist,
        opacity: safe(s.startOpacity, 0),
      },
      {
        x: 0,
        y: 0,
        opacity: 1,
        duration: safe(s.duration, 1),
        delay: safe(s.delay, 0),
        ease: safe(s.easing, 'power1.out'),
        ...opts,
      }
    );
  },

  reveal: (s, el, opts) => {
    const dist = s.distance ?? 100;
    const dir = s.direction ?? 'up';

    const startMap: Record<string, [number, number]> = {
      up: [0, dist],
      down: [0, -dist],
      left: [dist, 0],
      right: [-dist, 0],
      'up-right': [-dist, dist],
      'up-left': [dist, dist],
      'down-right': [-dist, -dist],
      'down-left': [dist, -dist],
    };

    const [xStart, yStart] = startMap[dir] ?? [0, dist];

    return gsap.fromTo(
      el,
      { x: xStart, y: yStart, clipPath: s.startClip ?? 'inset(100% 0 0 0)', opacity: s.startOpacity ?? 0 },
      {
        x: 0,
        y: 0,
        clipPath: 'inset(0 0 0 0)',
        opacity: 1,
        duration: s.duration ?? 1,
        delay: s.delay ?? 0,
        ease: s.easing ?? 'power1.inOut',
        ...opts,
      }
    );
  },

  shutters: (s, el, opts) => gsap.from(el, { opacity: 0, stagger: s.stagger ?? 0.1, ...opts }),

  slide: (s, el, opts) => {
    const dist = s.distance ?? 100;
    const dir = s.direction ?? 'up';

    const startMap: Record<string, [number, number]> = {
      up: [0, dist],
      down: [0, -dist],
      left: [dist, 0],
      right: [-dist, 0],
      'up-right': [-dist, dist],
      'up-left': [dist, dist],
      'down-right': [-dist, -dist],
      'down-left': [dist, -dist],
    };

    const [xStart, yStart] = startMap[dir] ?? [0, dist];

    return gsap.fromTo(
      el,
      { x: xStart, y: yStart, opacity: 0 },
      {
        x: 0,
        y: 0,
        opacity: 1,
        duration: s.duration ?? 1,
        delay: s.delay ?? 0,
        ease: s.easing ?? 'power1.inOut',
        ...opts,
      }
    );
  },

  blur: (s, el, opts) => gsap.fromTo(el, { filter: `blur(${s.blur_intensity ?? 5}px)` }, { filter: 'none', ...opts }),

  spin: (s, el, opts) =>
    gsap.to(el, {
      rotation: s.direction === 'counter-clockwise' ? -360 * (s.spin_count ?? 1) : 360 * (s.spin_count ?? 1),
      ...opts,
    }),

  turn: (s, el, opts) => gsap.from(el, { rotationY: s.turn_angle ?? 180, ...opts }),

  arc: (s, el, opts) => gsap.from(el, { x: s.distance ?? 100, rotation: s.arc_angle ?? 45, ...opts }),

  grow: (_s, el, opts) => gsap.from(el, { scale: 0, ...opts }),

  shrink: (s, el, opts) => gsap.from(el, { scale: s.scale ?? 2, ...opts }),

  wink: (s, el, opts) => gsap.from(el, { scaleX: s.wink_scale ?? 0.1, ...opts }),

  tilt: (s, el, opts) => gsap.from(el, { rotation: s.angle ?? 15, ...opts }),

  float: (s, el, opts) => gsap.from(el, { y: s.distance ?? 20, ...opts }),
};
